import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Users, Shield, Truck, Eye, EyeOff, X,
  CheckCircle2, ArrowRight, Loader2, LogOut, Settings,
  Zap, Star, MessageCircle, Send, Paperclip,
  TrendingUp, FileCheck, AlertCircle, Lock,
  ChevronLeft, ChevronRight, Award, Flame, Trophy,
  Calendar, DollarSign, FileText, Trash2, ExternalLink,
  Search, ChevronDown, Upload, Phone, Share2, Camera, Smile, ArrowLeft,
  Check, CheckCircle, XCircle, Download, FilePlus2, Layers,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

import { ChatRoom } from "./ChatRoom";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const DARK   = "#0A0A0A";
const CARD   = "#161618";
const MID    = "#1C1C1E";
const GREEN_SUCCESS = "#22c55e";

const HUB_META = {
  carrier:   { icon: Building2, label: "Carriers",   accent: "#FF4D00", glow: "255,77,0",    desc: "Bid on drivers",      badge: "HIRING",     badgeBg: "rgba(255,77,0,0.15)" },
  recruiter: { icon: Users,     label: "Recruiters", accent: "#F5C842", glow: "245,200,66",  desc: "Manage pipeline",     badge: "TALENT",     badgeBg: "rgba(245,200,66,0.15)" },
  safety:    { icon: Shield,    label: "Safety",     accent: "#38BDF8", glow: "56,189,248",  desc: "Vet & approve",       badge: "COMPLIANCE", badgeBg: "rgba(56,189,248,0.15)" },
  driver:    { icon: Truck,     label: "Drivers",    accent: "#4ADE80", glow: "74,222,128",  desc: "Your market value",   badge: "PROFILES",   badgeBg: "rgba(74,222,128,0.15)" },
  admin:     { icon: Star,      label: "Admin",      accent: "#A78BFA", glow: "167,139,250", desc: "Full access",         badge: "SYSTEM",     badgeBg: "rgba(167,139,250,0.15)" },
};

// ══════════════════════════════════════════════════════════════════════════════
function SafetyHub({ token, drivers, onUpdateDriverStatus, onRefresh, marketUser }) {
  const MARKET_STATUSES = [
    { key: "approved",     label: "Approve",    color: GREEN_SUCCESS, bg: "rgba(34,197,94,0.1)" },
    { key: "not_approved", label: "Not Approve", color: "#ef4444",    bg: "rgba(239,68,68,0.1)" },
  ];
  const PAGE_SIZE = 7;
  const [searchQ, setSearchQ] = useState("");
  const [page, setPage] = useState(0);
  const [docUploading, setDocUploading] = useState(null);
  const safetyDocRef = useRef(null);
  const [pendingDoc, setPendingDoc] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [cashbackEarned, setCashbackEarned] = useState(0);
  const [showCashbackIntro, setShowCashbackIntro] = useState(false);
  // Per-account reviews: { app_id: "approved" | "not_approved" }
  const [myReviews, setMyReviews] = useState({});

  // Load this safety user's own reviews on mount
  useEffect(() => {
    if (!token) return;
    api.marketSafetyMyReviews(token)
      .then((d) => setMyReviews(d.reviews || {}))
      .catch(() => {});
  }, [token]); // eslint-disable-line
  // ── Bulk selection ──
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Show cashback intro on first Safety login
  useEffect(() => {
    if (marketUser?.safety_cashback_first_login) setShowCashbackIntro(true);
  }, [marketUser?.safety_cashback_first_login]);

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function selectAll()    { setSelectedIds(new Set(filtered.map((d) => d.id))); }
  function clearSelection() { setSelectedIds(new Set()); }

  async function handleBulkStatus(status) {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    if (status === "approved") {
      const missing = ids.filter((id) => {
        const d = drivers.find((x) => x.id === id);
        return d && (!d.has_mvr || !d.has_psp);
      });
      if (missing.length > 0 && !window.confirm(`${missing.length} selected driver(s) are missing MVR or PSP. Bulk approve anyway?`)) return;
    }
    setBulkLoading(true);
    try {
      const res = await api.marketSafetyBulkReview(token, ids, status);
      const label = status === "approved" ? "Approved ✓" : "Not Approved";
      toast.success(`${res.updated} driver(s) marked as ${label} (your account only)`);
      // Update local state immediately
      setMyReviews((prev) => {
        const next = { ...prev };
        ids.forEach((id) => { next[id] = status; });
        return next;
      });
      clearSelection();
    } catch { toast.error("Bulk update failed."); } finally { setBulkLoading(false); }
  }

  async function handleSafetyDocUpload(file) {
    if (!file || !pendingDoc) return;
    setDocUploading(pendingDoc.driverId + pendingDoc.docType);
    try {
      await api.marketSafetyUploadDoc(token, pendingDoc.driverId, pendingDoc.docType, file);
      toast.success(`${pendingDoc.docType.toUpperCase()} uploaded successfully.`);
      // Award $10 cashback for MVR / PSP / Clearinghouse uploads
      if (["mvr", "psp", "clearinghouse"].includes(pendingDoc.docType)) {
        setCashbackEarned((prev) => prev + 10);
        toast.success(`+$10 cashback earned! Upload MVR, PSP & Clearinghouse to earn up to $30 per driver.`, { duration: 5000 });
      }
      onRefresh?.();
    } catch { toast.error("Upload failed."); } finally {
      setDocUploading(null); setPendingDoc(null);
    }
  }

  function handleApprove(driver, status) {
    const hasMvr = driver.has_mvr;
    const hasPsp = driver.has_psp;
    if (status === "approved" && (!hasMvr || !hasPsp)) {
      const missing = [!hasMvr && "MVR", !hasPsp && "PSP"].filter(Boolean).join(" + ");
      if (!window.confirm(`Warning: ${driver.first_name} is missing ${missing}. Approval strongly requires these documents. Approve anyway?`)) return;
    }
    api.marketSafetySetReview(token, driver.id, status)
      .then(() => {
        const label = status === "approved" ? "Approved ✓" : "Not Approved";
        toast.success(`${driver.first_name} marked ${label} (your account only)`);
        setMyReviews((prev) => ({ ...prev, [driver.id]: status }));
      })
      .catch(() => toast.error("Failed to save review."));
  }

  const filtered = drivers.filter((d) => {
    if (!searchQ.trim()) return true;
    const q = searchQ.toLowerCase();
    return (
      `${d.first_name} ${d.last_name}`.toLowerCase().includes(q) ||
      (d.state || "").toLowerCase().includes(q) ||
      (d.cdl_class || "").toLowerCase().includes(q) ||
      (d.market_status || "").toLowerCase().includes(q)
    );
  });
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  // Reset page + selection on search change
  useEffect(() => { setPage(0); clearSelection(); }, [searchQ]); // eslint-disable-line

  const allPageSelected = paged.length > 0 && paged.every((d) => selectedIds.has(d.id));
  const somePageSelected = paged.some((d) => selectedIds.has(d.id));

  return (
    <div className="space-y-8">
      {/* ── Cashback First-Login Intro Modal ── */}
      <AnimatePresence>
        {showCashbackIntro && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-md w-full p-8 text-center space-y-5"
              style={{ background: "#111", border: "1px solid rgba(56,189,248,0.35)" }}
            >
              <div className="w-14 h-14 mx-auto flex items-center justify-center" style={{ background: "rgba(56,189,248,0.12)", border: "1px solid rgba(56,189,248,0.3)" }}>
                <DollarSign className="w-7 h-7" style={{ color: "#38BDF8" }} />
              </div>
              <h2 className="font-head text-xl font-black uppercase tracking-[0.06em] text-white">Earn Cashback for Your Carrier</h2>
              <p className="text-sm text-white/60 leading-relaxed">
                Every time you upload an <strong className="text-white">MVR</strong>, <strong className="text-white">PSP</strong>, or <strong className="text-white">Clearinghouse</strong> document for a driver — your company earns <strong className="text-white">$10 cashback</strong> per document. Upload all three and earn <strong style={{ color: GOLD }}>$30</strong> per driver.
              </p>
              <div className="p-4 text-left space-y-2" style={{ background: "rgba(56,189,248,0.05)", border: "1px solid rgba(56,189,248,0.15)" }}>
                <p className="font-head text-[9px] uppercase tracking-[0.2em]" style={{ color: "#38BDF8" }}>How to Connect to Your Carrier</p>
                <p className="text-xs text-white/50 leading-relaxed">Make sure you entered your <strong className="text-white">full company name exactly</strong> when logging in (e.g., "ABC Trucking LLC"). Your carrier must use the <strong className="text-white">same company name</strong> when they access the Carrier Hub. That's how we link your cashback to their wallet.</p>
              </div>
              <p className="text-xs text-white/35">Cashback stacks across all drivers and can be redeemed as a discount off your next placement fee.</p>
              <button
                onClick={() => setShowCashbackIntro(false)}
                className="w-full py-3 font-head text-sm font-black uppercase tracking-[0.2em] transition-all hover:brightness-110"
                style={{ background: "#38BDF8", color: DARK }}
                data-testid="cashback-intro-close"
              >
                Got it — Start Uploading
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Cashback earned banner (session) ── */}
      {cashbackEarned > 0 && (
        <div className="flex items-center gap-3 px-4 py-3" style={{ background: "rgba(56,189,248,0.07)", border: "1px solid rgba(56,189,248,0.25)" }}>
          <DollarSign className="w-4 h-4 shrink-0" style={{ color: "#38BDF8" }} />
          <div>
            <p className="font-head text-xs font-bold text-white">+${cashbackEarned} cashback earned this session</p>
            <p className="text-[10px] text-white/35 mt-0.5">Stacks in your company's carrier wallet. Upload MVR, PSP & Clearinghouse to reach $30/driver.</p>
          </div>
        </div>
      )}

      {/* Section header */}
      <div>
        <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Safety Hub</p>
        <h2 className="font-head text-2xl font-black uppercase tracking-[0.04em] text-white">
          Compliance &amp; Vetting
        </h2>
      </div>

      {/* Chat with recruiters */}
      <div>
        <div className="mb-4">
          <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Live Communication</p>
          <h3 className="font-head text-xl font-black uppercase tracking-[0.04em]" style={{ color: GOLD }}>
            Recruiters Chat
          </h3>
        </div>
        <ChatRoom room="recruiters_safety" roomLabel="Safety · Recruiters" token={token} hub="safety" />
      </div>

      {/* Driver status management */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-1">Vetting Board</p>
            <h3 className="font-head text-xl font-black uppercase tracking-[0.04em] text-white">
              Driver Status Management
            </h3>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { key: "approved",     label: "Approved",      color: GREEN_SUCCESS, bg: "rgba(34,197,94,0.1)" },
              { key: "not_approved", label: "Not Approved",  color: "#ef4444",     bg: "rgba(239,68,68,0.1)" },
              { key: "applied",      label: "Pending",       color: ACCENT,        bg: "rgba(255,77,0,0.1)" },
            ].map((s) => (
              <span key={s.key} className="font-head text-[9px] uppercase tracking-[0.1em] px-2 py-1"
                style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}40` }}>
                {drivers.filter((d) => (d.market_status || "applied") === s.key).length} {s.label}
              </span>
            ))}
          </div>
        </div>

        <input
          ref={safetyDocRef}
          type="file"
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleSafetyDocUpload(f); e.target.value = ""; }}
        />

        {/* Search bar */}
        <div className="mb-3 relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
            style={{ color: "rgba(255,255,255,0.2)" }}
          />
          <input
            type="text"
            data-testid="safety-driver-search"
            placeholder="Search by name, state, CDL class, status…"
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            className="w-full bg-[#111] text-white text-sm pl-9 pr-4 py-2.5 focus:outline-none transition-colors"
            style={{ border: "1px solid rgba(255,255,255,0.08)", "::placeholder": { color: "rgba(255,255,255,0.2)" } }}
          />
          {searchQ && (
            <button
              onClick={() => setSearchQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {drivers.length === 0 ? (
          <div
            className="flex flex-col items-center py-16 text-center"
            style={{ background: MID, border: "1px solid rgba(255,255,255,0.06)" }}
          >
            <Shield className="w-10 h-10 mb-3 opacity-20" style={{ color: GOLD }} />
            <p className="font-head text-sm text-white/30 uppercase tracking-[0.2em]">No drivers to vet yet</p>
            <p className="text-white/20 text-xs mt-2">Drivers appear here once they apply and consent to Market participation.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center" style={{ background: MID, border: "1px solid rgba(255,255,255,0.06)" }}>
            <p className="text-white/30 text-sm">No drivers match "<span className="text-white/50">{searchQ}</span>"</p>
          </div>
        ) : (
          <>
            <div
              data-testid="safety-driver-status-table"
              style={{ background: MID, border: "1px solid rgba(255,255,255,0.07)" }}
            >
              {/* ── Bulk Action Bar (visible when any selected) ── */}
              {selectedIds.size > 0 && (
                <div
                  data-testid="safety-bulk-action-bar"
                  className="px-4 py-3 flex flex-wrap items-center gap-3"
                  style={{ background: "rgba(245,200,66,0.07)", borderBottom: "1px solid rgba(245,200,66,0.2)" }}
                >
                  <span className="font-head text-xs font-black uppercase tracking-[0.15em]" style={{ color: GOLD }}>
                    {selectedIds.size} driver{selectedIds.size !== 1 ? "s" : ""} selected
                  </span>
                  <div className="flex gap-2 flex-wrap ml-auto">
                    <button
                      data-testid="safety-bulk-approve-btn"
                      onClick={() => handleBulkStatus("approved")}
                      disabled={bulkLoading}
                      className="px-4 py-2 font-head text-[10px] uppercase tracking-[0.12em] disabled:opacity-50 hover:brightness-110 transition-all flex items-center gap-1.5"
                      style={{ background: "rgba(34,197,94,0.18)", color: GREEN_SUCCESS, border: "1px solid rgba(34,197,94,0.4)" }}
                    >
                      {bulkLoading ? <><Loader2 className="w-3 h-3 animate-spin" /> Processing…</> : <><CheckCircle className="w-3 h-3" /> Approve All</>}
                    </button>
                    <button
                      data-testid="safety-bulk-not-approve-btn"
                      onClick={() => handleBulkStatus("not_approved")}
                      disabled={bulkLoading}
                      className="px-4 py-2 font-head text-[10px] uppercase tracking-[0.12em] disabled:opacity-50 hover:brightness-110 transition-all flex items-center gap-1.5"
                      style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.35)" }}
                    >
                      {bulkLoading ? "…" : <><XCircle className="w-3 h-3" /> Not Approve All</>}
                    </button>
                    <button
                      data-testid="safety-bulk-clear-btn"
                      onClick={clearSelection}
                      className="px-3 py-2 font-head text-[10px] uppercase tracking-[0.1em] text-white/35 hover:text-white/60 transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {/* Table header */}
              <div
                className="px-4 py-2.5 flex items-center gap-3 font-head text-[9px] uppercase tracking-[0.2em] text-white/30"
                style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}
              >
                {/* Select All / None toggle */}
                <button
                  data-testid="safety-select-all-toggle"
                  onClick={() => allPageSelected ? clearSelection() : selectAll()}
                  title={allPageSelected ? "Deselect all" : "Select all filtered"}
                  className="w-5 h-5 flex items-center justify-center border transition-all shrink-0 hover:border-orange-500/50"
                  style={{
                    background: allPageSelected ? ACCENT : somePageSelected ? "rgba(255,77,0,0.1)" : "transparent",
                    borderColor: allPageSelected ? ACCENT : somePageSelected ? "rgba(255,77,0,0.4)" : "rgba(255,255,255,0.2)",
                  }}
                >
                  {allPageSelected && <Check className="w-3 h-3 text-white" />}
                  {somePageSelected && !allPageSelected && <span className="w-2 h-0.5 block" style={{ background: ACCENT }} />}
                </button>
                <span className="flex-1">Driver</span>
                <span>Set Status</span>
              </div>

              <div className="divide-y divide-white/05">
                {paged.map((d) => {
                  // Use this safety user's own review, not the global market_status
                  const mStatus = myReviews[d.id] || "applied";
                  const currentStatusMeta = MARKET_STATUSES.find((s) => s.key === mStatus) || { label: "Pending", color: ACCENT };
                  const review = d.safety_review;
                  const missingDocs = (!d.has_mvr || !d.has_psp) ? [!d.has_mvr && "MVR", !d.has_psp && "PSP"].filter(Boolean) : [];
                  const isExpanded = expandedId === d.id;
                  return (
                    <div key={d.id}>
                      <div className="px-4 py-4 flex flex-wrap items-start justify-between gap-4">
                        {/* Row checkbox */}
                        <button
                          data-testid={`safety-select-${d.id}`}
                          onClick={() => toggleSelect(d.id)}
                          className="mt-0.5 w-5 h-5 flex items-center justify-center border transition-all shrink-0 hover:border-orange-500/50"
                          style={{
                            background: selectedIds.has(d.id) ? ACCENT : "transparent",
                            borderColor: selectedIds.has(d.id) ? ACCENT : "rgba(255,255,255,0.2)",
                          }}
                        >
                          {selectedIds.has(d.id) && <Check className="w-3 h-3 text-white" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-head text-sm font-bold text-white">{d.first_name} {d.last_name}</p>
                            <span
                              className="font-head text-[9px] uppercase tracking-[0.1em] px-2 py-0.5"
                              style={{ background: `${currentStatusMeta.color}15`, color: currentStatusMeta.color, border: `1px solid ${currentStatusMeta.color}40` }}
                            >
                              {currentStatusMeta.label || mStatus.replace("_", " ")}
                            </span>
                            {review && (
                              <span className="font-head text-[8px] uppercase tracking-[0.08em] px-1.5 py-0.5"
                                style={{ background: "rgba(56,189,248,0.08)", color: "#38BDF8", border: "1px solid rgba(56,189,248,0.2)" }}>
                                By: {review.reviewer_name}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            <span className="text-xs text-white/40">
                              {d.state} · {d.cdl_class || "CDL-A"} · {d.position || "—"}
                            </span>
                            <div className="flex gap-1">
                              {[{ k: "CDL", v: d.has_cdl }, { k: "CDL-B", v: d.has_cdl_back }, { k: "MED", v: d.has_medical }, { k: "MVR", v: d.has_mvr }, { k: "PSP", v: d.has_psp }, { k: "CLR", v: d.has_clearinghouse }].map((doc) => (
                                <span key={doc.k} className="font-head text-[8px] uppercase px-1 py-0.5"
                                  style={{ background: doc.v ? "rgba(255,77,0,0.1)" : "rgba(255,255,255,0.04)", color: doc.v ? ACCENT : "#555", border: `1px solid ${doc.v ? "rgba(255,77,0,0.3)" : "#2a2a2a"}` }}>
                                  {doc.v ? "✓" : "○"} {doc.k}
                                </span>
                              ))}
                            </div>
                          </div>
                          {missingDocs.length > 0 && (
                            <p className="text-[9px] mt-1" style={{ color: "#ef4444" }}>
                              Missing: {missingDocs.join(", ")} — upload required before approval
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1.5 shrink-0">
                          {/* Always show both Approve + Not Approve */}
                          <div className="flex gap-1.5">
                            {MARKET_STATUSES.map((s) => {
                              const isCurrent = mStatus === s.key;
                              return (
                                <button
                                  key={s.key}
                                  onClick={() => !isCurrent && handleApprove(d, s.key)}
                                  data-testid={`safety-status-${d.id}-${s.key}`}
                                  disabled={isCurrent}
                                  className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-all hover:brightness-110 disabled:cursor-default"
                                  style={{
                                    background: isCurrent ? s.bg : "transparent",
                                    color: isCurrent ? s.color : "rgba(255,255,255,0.35)",
                                    border: `1px solid ${isCurrent ? s.color + "60" : "rgba(255,255,255,0.12)"}`,
                                    opacity: isCurrent ? 1 : 0.7,
                                  }}
                                >
                                  {isCurrent ? "● " : "→ "}{s.label}
                                </button>
                              );
                            })}
                          </div>
                          <button
                            onClick={() => setExpandedId(isExpanded ? null : d.id)}
                            className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-all text-center"
                            style={{ borderColor: isExpanded ? "rgba(255,77,0,0.4)" : "rgba(255,255,255,0.1)", color: isExpanded ? ACCENT : "rgba(255,255,255,0.3)" }}>
                            {isExpanded ? "Close ▴" : "Upload Docs ▾"}
                          </button>
                        </div>
                      </div>

                      {/* Doc upload expand panel */}
                      {isExpanded && (
                        <div className="px-4 pb-4 flex gap-2 flex-wrap" style={{ borderTop: "1px solid rgba(255,77,0,0.1)", background: "rgba(255,77,0,0.02)" }}>
                          <p className="w-full font-head text-[8px] uppercase tracking-[0.2em] text-white/25 pt-3 pb-1">Upload Documents</p>
                          {/* Non-medical uploads (single per type) */}
                          {[
                            { type: "cdl",           label: "CDL Front" },
                            { type: "cdl_back",      label: "CDL Back" },
                            { type: "previous_cdl_1", label: "Prev CDL 1" },
                            { type: "previous_cdl_2", label: "Prev CDL 2" },
                            { type: "mvr",           label: "MVR" },
                            { type: "psp",           label: "PSP" },
                            { type: "clearinghouse", label: "Clearinghouse" },
                          ].map((doc) => (
                            <button
                              key={doc.type}
                              disabled={!!docUploading}
                              onClick={() => { setPendingDoc({ driverId: d.id, docType: doc.type }); safetyDocRef.current?.click(); }}
                              className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors hover:border-orange-500/40 disabled:opacity-50"
                              style={{ borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.4)" }}
                              data-testid={`safety-upload-${d.id}-${doc.type}`}>
                              {docUploading === d.id + doc.type ? "Uploading…" : `Upload ${doc.label}`}
                            </button>
                          ))}

                          {/* Medical — multi-page upload */}
                          <div className="w-full mt-2 pt-2 flex flex-wrap gap-2 items-center" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                            <p className="w-full font-head text-[8px] uppercase tracking-[0.2em] text-white/25 pb-1 flex items-center gap-2">
                              <Layers className="w-3 h-3" /> Medical Certificate
                              {d.has_medical && d.medical_page_count > 0 && (
                                <span className="px-2 py-0.5 text-[9px] font-black"
                                  style={{ background: "rgba(245,200,66,0.12)", color: "#F5C842", border: "1px solid rgba(245,200,66,0.25)" }}>
                                  {d.medical_page_count} {d.medical_page_count === 1 ? "PAGE" : "PAGES"}
                                </span>
                              )}
                            </p>
                            <button
                              disabled={!!docUploading}
                              onClick={() => { setPendingDoc({ driverId: d.id, docType: "medical" }); safetyDocRef.current?.click(); }}
                              className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors hover:border-orange-500/40 disabled:opacity-50 flex items-center gap-1.5"
                              style={{ borderColor: "rgba(255,165,0,0.3)", color: "rgba(255,165,0,0.8)", background: "rgba(255,165,0,0.05)" }}
                              data-testid={`safety-upload-${d.id}-medical`}>
                              <FilePlus2 className="w-3 h-3" />
                              {docUploading === d.id + "medical" ? "Uploading…" : (d.has_medical ? `Add Page ${(d.medical_page_count || 0) + 1}` : "Upload Medical")}
                            </button>
                            {/* Delete last medical page */}
                            {d.has_medical && d.medical_page_count > 0 && (
                              <button
                                disabled={!!docUploading}
                                onClick={async () => {
                                  const idx = (d.medical_page_count || 1) - 1;
                                  try {
                                    await api.marketDeleteMedicalPage(token, d.id, idx);
                                    toast.success(`Medical page ${idx + 1} removed`);
                                    onRefresh?.();
                                  } catch { toast.error("Failed to remove page"); }
                                }}
                                className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border transition-colors hover:border-red-500/40 disabled:opacity-50 flex items-center gap-1.5"
                                style={{ borderColor: "rgba(239,68,68,0.2)", color: "rgba(239,68,68,0.6)" }}
                                data-testid={`safety-delete-medical-page-${d.id}`}>
                                <Trash2 className="w-3 h-3" /> Remove Last Page
                              </button>
                            )}
                          </div>

                          {/* ── Download existing documents ── */}
                          {[
                            { type: "cdl",           label: "CDL Front",     has: d.has_cdl },
                            { type: "cdl_back",      label: "CDL Back",      has: d.has_cdl_back },
                            { type: "previous_cdl_1", label: "Prev CDL 1",   has: d.has_previous_cdl_1 },
                            { type: "previous_cdl_2", label: "Prev CDL 2",   has: d.has_previous_cdl_2 },
                            { type: "mvr",           label: "MVR",           has: d.has_mvr },
                            { type: "psp",           label: "PSP",           has: d.has_psp },
                            { type: "clearinghouse", label: "Clearinghouse", has: d.has_clearinghouse },
                          ].some((doc) => doc.has) && (
                            <>
                              <p className="w-full font-head text-[8px] uppercase tracking-[0.2em] text-white/25 pt-2 pb-1">Download Documents</p>
                              {[
                                { type: "cdl",           label: "CDL Front",     has: d.has_cdl },
                                { type: "cdl_back",      label: "CDL Back",      has: d.has_cdl_back },
                                { type: "previous_cdl_1", label: "Prev CDL 1",   has: d.has_previous_cdl_1 },
                                { type: "previous_cdl_2", label: "Prev CDL 2",   has: d.has_previous_cdl_2 },
                                { type: "mvr",           label: "MVR",           has: d.has_mvr },
                                { type: "psp",           label: "PSP",           has: d.has_psp },
                                { type: "clearinghouse", label: "Clearinghouse", has: d.has_clearinghouse },
                              ].filter((doc) => doc.has).map((doc) => (
                                <button
                                  key={`dl-${doc.type}`}
                                  data-testid={`safety-download-${d.id}-${doc.type}`}
                                  onClick={async () => {
                                    try {
                                      const blob = await api.marketDownloadDoc(token, d.id, doc.type);
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement("a");
                                      a.href = url;
                                      a.download = `${d.first_name}-${d.last_name}-${doc.type}`.toLowerCase().replace(/\s+/g, "-");
                                      a.click();
                                      URL.revokeObjectURL(url);
                                      toast.success(`${doc.label} downloaded`);
                                    } catch {
                                      toast.error(`Failed to download ${doc.label}`);
                                    }
                                  }}
                                  className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border flex items-center gap-1.5 transition-colors hover:border-sky-400/40"
                                  style={{ borderColor: "rgba(56,189,248,0.25)", color: "rgba(56,189,248,0.85)", background: "rgba(56,189,248,0.04)" }}
                                >
                                  <Download className="w-3 h-3" /> {doc.label}
                                </button>
                              ))}
                            </>
                          )}
                          {/* Medical download — ZIP if multiple pages */}
                          {d.has_medical && (
                            <button
                              data-testid={`safety-download-${d.id}-medical`}
                              onClick={async () => {
                                try {
                                  const blob = await api.marketDownloadDoc(token, d.id, "medical");
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement("a");
                                  a.href = url;
                                  const isZip = d.medical_page_count > 1;
                                  a.download = `${d.first_name}-${d.last_name}-medical${isZip ? ".zip" : ""}`.toLowerCase().replace(/\s+/g, "-");
                                  a.click();
                                  URL.revokeObjectURL(url);
                                  toast.success(d.medical_page_count > 1 ? `Medical ZIP (${d.medical_page_count} pages) downloaded` : "Medical downloaded");
                                } catch { toast.error("Failed to download Medical"); }
                              }}
                              className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border flex items-center gap-1.5 transition-colors hover:border-sky-400/40"
                              style={{ borderColor: "rgba(56,189,248,0.25)", color: "rgba(56,189,248,0.85)", background: "rgba(56,189,248,0.04)" }}
                            >
                              <Download className="w-3 h-3" />
                              {d.medical_page_count > 1 ? `Medical (${d.medical_page_count} pages) ZIP` : "Medical Cert"}
                            </button>
                          )}
                          {/* DQ Packet download */}
                          <button
                            data-testid={`safety-dq-packet-btn-${d.id}`}
                            onClick={async () => {
                              try {
                                const blob = await api.marketDownloadDQPacket(token, d.id);
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = `hylas-dq-${d.first_name}-${d.last_name}.pdf`.toLowerCase().replace(/\s+/g, "-");
                                a.click();
                                URL.revokeObjectURL(url);
                                toast.success("DQ Packet downloaded");
                              } catch {
                                toast.error("Failed to download DQ packet");
                              }
                            }}
                            className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border flex items-center gap-1.5 transition-colors hover:border-amber-400/40"
                            style={{ borderColor: "rgba(245,200,66,0.25)", color: GOLD, background: "rgba(245,200,66,0.05)" }}
                          >
                            <Download className="w-3 h-3" /> DQ Packet PDF
                          </button>
                          {/* PII Clear All for this driver */}
                          <button
                            data-testid={`safety-pii-clear-all-${d.id}`}
                            onClick={async () => {
                              try {
                                await api.marketPiiClearAll(token, d.id);
                                toast.success("All docs PII-cleared — carriers can now download");
                                onRefresh?.();
                              } catch {
                                toast.error("PII clear failed");
                              }
                            }}
                            className="px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.12em] border flex items-center gap-1.5 transition-colors hover:border-green-400/40"
                            style={{ borderColor: "rgba(34,197,94,0.25)", color: "#22C55E", background: "rgba(34,197,94,0.04)" }}
                          >
                            <CheckCircle className="w-3 h-3" /> PII Clear All Docs
                          </button>
                          {/* Per-doc PII badges — CDL back / prev CDL only shown when uploaded */}
                          {[
                            { key: "cdl",            label: "CDL",      always: true },
                            { key: "cdl_back",       label: "CDL BACK", always: false, has: d.has_cdl_back },
                            { key: "previous_cdl_1", label: "PREV 1",   always: false, has: d.has_previous_cdl_1 },
                            { key: "previous_cdl_2", label: "PREV 2",   always: false, has: d.has_previous_cdl_2 },
                            { key: "medical",        label: "MED",      always: true },
                            { key: "mvr",            label: "MVR",      always: true },
                            { key: "psp",            label: "PSP",      always: true },
                            { key: "clearinghouse",  label: "CLR",      always: true },
                          ].filter(dt => dt.always || dt.has).map(({ key: docType, label }) => {
                            const cleared = d.pii_cleared?.[docType] === true;
                            return (
                              <button
                                key={docType}
                                data-testid={`safety-pii-toggle-${d.id}-${docType}`}
                                onClick={async () => {
                                  try {
                                    await api.marketPiiClearToggle(token, d.id, docType, !cleared);
                                    toast.success(`${label} PII ${!cleared ? "cleared" : "locked"}`);
                                    onRefresh?.();
                                  } catch {
                                    toast.error("Toggle failed");
                                  }
                                }}
                                className="px-2 py-1 font-head text-[8px] uppercase tracking-[0.1em] border transition-colors"
                                style={{
                                  borderColor: cleared ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.1)",
                                  color: cleared ? "#22C55E" : "rgba(255,255,255,0.3)",
                                  background: cleared ? "rgba(34,197,94,0.05)" : "transparent",
                                }}
                              >
                                {cleared ? "✓" : "○"} {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 px-1">
                <span className="text-[10px] text-white/30 font-head uppercase tracking-[0.1em]">
                  {filtered.length} driver{filtered.length !== 1 ? "s" : ""} · page {page + 1} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <button
                    data-testid="safety-page-prev"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="w-7 h-7 flex items-center justify-center disabled:opacity-30 transition-colors hover:text-white text-white/40"
                    style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      data-testid={`safety-page-${i}`}
                      onClick={() => setPage(i)}
                      className="w-7 h-7 flex items-center justify-center font-head text-[10px] transition-colors"
                      style={{
                        background: i === page ? GOLD : "transparent",
                        color: i === page ? DARK : "rgba(255,255,255,0.3)",
                        border: `1px solid ${i === page ? GOLD : "rgba(255,255,255,0.1)"}`,
                      }}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    data-testid="safety-page-next"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page === totalPages - 1}
                    className="w-7 h-7 flex items-center justify-center disabled:opacity-30 transition-colors hover:text-white text-white/40"
                    style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


export { SafetyHub };
